import React, { useEffect, useRef, useState } from "react";

// Error Handling
import ErrorBoundary from "orion-components/ErrorBoundary";

// material-ui
import {
    List,
    Dialog,
    CircularProgress,
    Button,
    DialogActions,
    IconButton,
    ListItem,
    ListItemText,
    ListItemIcon
} from "@mui/material";
import { makeStyles } from "@mui/styles";

// components
import TypeAheadFilter from "./components/TypeAheadFilter";
// import * as actionCreators from "../../actions/index";

// misc

//virtualized list
import { List as VirtualList, AutoSizer } from "react-virtualized";
import { Translate, getTranslation } from "orion-components/i18n";
import { useSelector, useDispatch } from "react-redux";
import { getDir } from "orion-components/i18n/Config/selector";
import CloseIcon from "@mui/icons-material/Close";
import { includes } from "lodash";
import PropTypes from "prop-types";
import { CameraCluster } from "orion-components/CBComponents/Icons";
import SpriteIcon from "orion-components/SharedComponents/SpriteIcon/SpriteIcon";
import { ShapeSelectorIconConfig } from "orion-components/SharedComponents";

const html = document.getElementsByTagName("html")[0];

const getOverrides = (dir, isMobile) => ({
    paperProps: {
        width: "100%",
        borderRadius: "2px"
    },
    dialogItemIcon: {
        height: 27,
        width: 27,
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        display: "flex",
        backgroundColor: "#4CB5F4",
        borderRadius: "5px",
        opacity: 1,
        cursor: "pointer"
    },
    listItem: {
        height: 49,
        cursor: "pointer",
        ...(dir === "rtl" && { flexDirection: "row-reverse", textAlign: "right" })
    },
    listItemSX: {
        width: "27px !important",
        height: "27px !important",
        minWidth: "27px !important",
        marginRight: "10px !important"
    },
    listItemIcon: {
        margin: "0px 10px",
        ...(dir === "rtl" && { flexDirection: "row-reverse" })
    },
    buttonStyles: isMobile
        ? {
            fontSize: "13px"
        }
        : {},
    dialogTitle: {
        fontStyle: "normal",
        fontWeight: "medium",
        fontSize: "14px",
        lineHeight: "16px",
        fontFamily: "Roboto",
        letterSpacing: "0px",
        color: "#FFFFFF",
        opacity: 1,
        ...(dir === "ltr" && { marginLeft: 21, textAlign: "left" }),
        ...(dir === "rtl" && { marginRight: 21, textAlign: "right" }),
        marginTop: -5
    },
    closeButtonWrapper: {
        width: 20,
        height: 20,
        marginTop: 19,
        ...(dir === "ltr" && { marginRight: 14 }),
        ...(dir === "rtl" && { marginLeft: 14 })
    },
    closeButton: {
        width: 20,
        height: 20,
        color: "#FFFFFF"
    },
    rowRenderListItemText: {
        ...(dir === "ltr" && { textAlign: "left" }),
        ...(dir === "rtl" && { textAlign: "right" }),
        fontStyle: "normal",
        fontWeight: "normal",
        fontSize: "12px",
        lineHeight: "14px",
        fontFamily: "Roboto",
        letterSpacing: "0px",
        color: "#FFFFFF",
        opacity: 1
    },
    dialogCloseIcon: { display: "grid", justifyContent: "end" }
});

const useStyles = makeStyles({
    scrollPaper: {
        width: "75%",
        margin: "0px auto"
    }
});

const SelectMultipleDialog = ({
    dialogItems,
    dialogTitle,
    dialogSelectedItemsId,
    getSelectedOptions,
    dialogItemIcon,
    dialogItemTextPropPath,
    open,
    closeDialog
}) => {
    const dispatch = useDispatch();
    const classes = useStyles();
    const dir = useSelector((state) => getDir(state) || "ltr");
    // const { clearSearchResults, typeAheadFilter } = actionCreators;

    const virtualListContainerRef = useRef(null);

    // const isQuerying = useSelector((state) => state.appState.profilePage.isQuerying);
    // const error = useSelector((state) => state.appState.profilePage.queryDialogError);

    const [typeAheadFilter, setTypeAheadFilter] = useState("");

    const [state, setState] = useState({
        selectedItems: [],
        mounted: false
    });
    const isMobile = window.matchMedia("(max-width: 600px)").matches;
    const overrides = getOverrides(dir, isMobile); // Get overrides using the dir

    const getDialogIcons = (iconType, entity) => {
        switch (iconType) {
            case "cameraClusters":
                return <CameraCluster />;
            case "lists":
                return <SpriteIcon iconName="icon_view-list" customWidth="15px" />;
            case "shapes": {
                let iconName;
                if (entity.entityData?.properties?.isCircle) {
                    iconName = "circle";
                } else if (entity.entityData?.properties?.isRectangle) {
                    iconName = "rectangle";
                } else if (entity.entityData?.geometry?.type === "Polygon") {
                    iconName = "polygon";
                } else if (entity.entityData?.geometry?.type === "LineString") {
                    iconName = "line";
                }

                return ShapeSelectorIconConfig(iconName, { width: 20 });
            }
            default:
                return <></>;
        }
    };

    // Enter to submit
    const _handleKeyDown = (event) => {
        if (event.key === "Enter" && open) {
            handleSaveClick();
        }
    };

    useEffect(() => {
        setState((prevState) => ({ ...prevState, mounted: true }));
        return () => {
            // Allow background scrolling on dialog close
            html.style.position = "static";
            html.style.width = "auto";
            document.removeEventListener("keydown", _handleKeyDown);
            setTypeAheadFilter("");
            // dispatch(clearSearchResults());
        };
    }, []);

    if (!state.mounted) {
        // Prevent background scrolling when dialog is open.
        html.style.position = "fixed";
        html.style.width = "100%";
        document.addEventListener("keydown", _handleKeyDown);
        setState((prevState) => ({ ...prevState, mounted: true }));
    }

    const handleSaveClick = () => {
        getSelectedOptions(state.selectedItems);
        setState((prevState) => ({ ...prevState, selectedItems: [] }));
        setTypeAheadFilter("");
        // dispatch(typeAheadFilter(""));
        closeDialog();
    };

    const handleCancelClick = () => {
        setState((prevState) => ({ ...prevState, selectedItems: [] }));
        setTypeAheadFilter("");
        // dispatch(typeAheadFilter(""));
        closeDialog();
    };

    const dialogActionButtons = [
        <Button
            sx={{ color: "#979798", fontSize: 14, ...overrides.buttonStyles }}
            onClick={handleCancelClick}
            variant="text"
            key="cancel-action-button" data-testid="orion-components-shared-components-select-multiple-dialog-select-multiple-dialog-button"
        >
            <Translate value="global.sharedComponents.selectMultipleDialog.cancel" />
        </Button>,
        <Button
            sx={{ fontSize: 14, ...overrides.buttonStyles }}
            onClick={() => handleSaveClick()}
            key="save-action-button"
            variant="text"
            disabled={!state.selectedItems.length} data-testid="orion-components-shared-components-select-multiple-dialog-select-multiple-dialog-button-2"
        >
            <Translate value="global.sharedComponents.selectMultipleDialog.add" />
        </Button>
    ];

    const handleSelect = (item) => {
        const tArray = state.selectedItems;
        const index = tArray.indexOf(item);
        tArray.indexOf(item) > -1 ? tArray.splice(index, 1) : tArray.push(item);
        setState((prevState) => ({ ...prevState, selectedItems: tArray }));
    };

    let dialogRows = [];
    let rowRenderer = [];

    dialogRows = Array.isArray(dialogItems)
        ? dialogItems
            .filter((item) => {
                return !dialogSelectedItemsId.includes(item.id);
            })
            // filter by typeahead
            .filter((item) => {
                if (typeAheadFilter === "") return item;
                else {
                    const name = item.name || item?.entityData?.properties?.name;
                    if (includes(name.toLowerCase(), typeAheadFilter.toLowerCase())) return item;
                    else return false;
                }
            })
        : [];

    rowRenderer = dialogRows.map((item) => {
        const primaryText = item[dialogItemTextPropPath] || item?.entityData?.properties?.name;

        return (
            <ListItem
                key={item.id}
                onClick={() => handleSelect(item)}
                style={{
                    ...overrides.listItem,
                    backgroundColor: state.selectedItems.indexOf(item) > -1 ? "#2C2D2F" : "transparent"
                }}
                sx={{
                    "& .MuiListItemIcon-root": overrides.listItemSX
                }} data-testid="orion-components-shared-components-select-multiple-dialog-select-multiple-dialog-list-item"
            >
                <ListItemIcon style={overrides.listItemIcon}>
                    <div style={overrides.dialogItemIcon}>{getDialogIcons(dialogItemIcon, item)}</div>
                </ListItemIcon>
                <ListItemText
                    primary={primaryText}
                    primaryTypographyProps={{
                        style: overrides.rowRenderListItemText
                    }}
                    sx={{ margin: "0px" }}
                />
            </ListItem>
        );
    });

    const renderRows = ({ key, index, style }) => {
        return (
            <div key={key} style={style}>
                {rowRenderer[index]}
            </div>
        );
    };

    return (
        <Dialog
            PaperProps={{ className: "bgColorOverride", sx: overrides.paperProps }}
            open={open}
            onClose={handleCancelClick}
            classes={{ scrollPaper: classes.scrollPaper }} data-testid="orion-components-shared-components-select-multiple-dialog-select-multiple-dialog-dialog"
        >
            <div id="selectMultipleDialog-closeIcon" style={overrides.dialogCloseIcon}>
                <IconButton style={overrides.closeButtonWrapper} onClick={handleCancelClick} data-testid="orion-components-shared-components-select-multiple-dialog-select-multiple-dialog-icon-button">
                    <CloseIcon style={overrides.closeButton} />
                </IconButton>
            </div>
            <div id="selectMultipleDialog-title" style={overrides.dialogTitle}>
                {dialogTitle}
            </div>
            <div style={{ display: "flex", flexDirection: "column", height: "70vh" }} ref={virtualListContainerRef}>
                <List sx={overrides.list} data-testid="orion-components-shared-components-select-multiple-dialog-select-multiple-dialog-list">
                    {dialogItems?.length ?
                        <div style={{ padding: "20px 14px" }}>
                            <ErrorBoundary>
                                <TypeAheadFilter
                                    className="typeAheadFilter"
                                    placeholder={getTranslation("global.sharedComponents.selectMultipleDialog.search")}
                                    dispatch={dispatch}
                                    disableDarkTheme={true}
                                    typeAheadFilter={typeAheadFilter}
                                    setTypeAheadFilter={setTypeAheadFilter}
                                />
                            </ErrorBoundary>
                        </div> : null
                    }
                    {dialogRows?.length > 0 ? (
                        <AutoSizer disableHeight>
                            {({ width }) => (
                                <VirtualList
                                    rowCount={dialogRows?.length ?? 0}
                                    autoContainerWidth={true}
                                    rowHeight={49}
                                    width={width}
                                    height={virtualListContainerRef?.current?.clientHeight - 100} //This calculation introduces a margin at the bottom between the dialog actions and the list.
                                    rowRenderer={renderRows}
                                    overscanRowCount={1} // cSpell:ignore overscan
                                />
                            )}
                        </AutoSizer>
                    ) : (
                        <div style={{ display: "flex", justifyContent: "center", color: "#fff", fontSize: 14 }}>
                            <Translate value={`global.sharedComponents.selectMultipleDialog.no_${dialogItemIcon || "entities"}`} />
                        </div>
                    )}
                </List>
            </div>

            <DialogActions data-testid="orion-components-shared-components-select-multiple-dialog-select-multiple-dialog-dialog-actions">{dialogActionButtons}</DialogActions>
        </Dialog>
    );
};

SelectMultipleDialog.propTypes = {
    dialogItems: PropTypes.array.isRequired,
    dialogTitle: PropTypes.string.isRequired,
    dialogSelectedItemsId: PropTypes.array.isRequired,
    getSelectedOptions: PropTypes.func.isRequired,
    dialogItemIcon: PropTypes.any,
    dialogItemTextPropPath: PropTypes.any.isRequired,
    open: PropTypes.bool.isRequired,
    closeDialog: PropTypes.func.isRequired
};

export default SelectMultipleDialog;
